/*
  Einfaches Monatsraster: Mitarbeitende x Tage.
*/

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
// Dasselbe Symbol wie in /settings fuer "Abteilungen & Schichten".
import IconDepartment from "@mui/icons-material/CorporateFareOutlined";
import CelebrationOutlinedIcon from "@mui/icons-material/CelebrationOutlined";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import PendingActionsOutlinedIcon from "@mui/icons-material/PendingActionsOutlined";
import StickyNote2OutlinedIcon from "@mui/icons-material/StickyNote2Outlined";
import AppPersonBadge from "../../../../design-system/components/AppPersonBadge";
import { IconHoliday } from "../../../../design-system/icons";
import { isDemandDragEntry } from "../../domain/model/demand";
import { shortenFullName } from "../../domain/model/employeeLabels";
import { SHIFT_DISPLAY_MODES, getShiftDisplayParts, normalizeShiftDisplayMode } from "../../domain/model/shiftDisplay";
import DayCommentPopover from "./DayCommentPopover";
import DemandRow from "./DemandRow";
import { getRevealDelta } from "./gridScroll";
import ShiftplanTooltip from "../tooltips/ShiftplanTooltip";
import ShiftplanV2State from "../ui/ShiftplanV2State";

const GRID_EMPLOYEE_COLUMN_MAX_WIDTH = 232;
const GRID_DAY_COLUMN_MIN_WIDTH = 65;
/*
  Wie viele Einträge eine Zelle zeigt, bevor der "+N"-Knopf übernimmt.

  Im Monat bleibt es bei drei: 31 Spalten machen jede zusätzliche Kachelreihe
  teuer, und die Zeilenhöhe gilt für die ganze Zeile — die vollste Zelle
  bestimmt sie. Die Woche hat nur 7 Spalten und Platz nach unten, dort passen
  sechs.
*/
const MAX_COLLAPSED_SHIFTS_PER_CELL_MONTH = 3;
const MAX_COLLAPSED_SHIFTS_PER_CELL_WEEK = 6;
const FULL_CIRCLE_DEGREES = 360;
const SPANNING_ENTRY_HEIGHT = 22;
const SPANNING_ENTRY_GAP = 3;
const SPANNING_ENTRY_TOP = 3;
const MILLISECONDS_PER_DAY = 86_400_000;
/*
  Ein Ziel ins Bild holen (siehe `focusCell`): kurz abwarten, damit das reine
  Überfahren einer Liste nicht scrollt, und einen Rand freihalten, damit die
  Zelle nicht am Rahmen klebt.
*/
const FOCUS_SCROLL_DELAY_MS = 140;
const FOCUS_SCROLL_MARGIN = 24;
const EMPTY_WORKLOAD = {
  label: "0h / 0h",
  tone: "neutral",
  progressDegrees: 0,
  overageDegrees: 0,
  vacationDays: 0,
  vacationLabel: "0 Ferientage",
};

function getGridMinWidth(dayCount) {
  return GRID_EMPLOYEE_COLUMN_MAX_WIDTH + dayCount * GRID_DAY_COLUMN_MIN_WIDTH;
}

function prefersReducedMotion() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function degreesToPercent(degrees) {
  const value = Number(degrees || 0);
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(100, (value / FULL_CIRCLE_DEGREES) * 100));
}

function stopGridPointerSelection(event) {
  event.stopPropagation();
}

function prepareEntryDragImage(event) {
  const dataTransfer = event?.dataTransfer;
  const source = event?.currentTarget;
  if (!dataTransfer?.setDragImage || !source?.cloneNode || typeof document === "undefined") return;

  const rect = source.getBoundingClientRect?.();
  const width = Number(rect?.width || 0);
  const height = Number(rect?.height || 0);
  if (!width || !height) return;

  const clone = source.cloneNode(true);
  clone.setAttribute("aria-hidden", "true");
  clone.classList.add("spv2-entry-drag-image");
  clone.classList.remove(
    "is-dragging",
    "is-selected",
    "is-copied",
    "is-release-preview-dimmed",
    "is-release-preview-target"
  );
  clone.style.width = `${width}px`;
  clone.style.height = `${height}px`;
  clone.style.pointerEvents = "none";
  clone.style.opacity = "1";
  clone.style.filter = "none";
  clone.style.transform = "none";
  clone.style.transition = "none";

  document.body.appendChild(clone);

  const clientX = Number(event.clientX);
  const clientY = Number(event.clientY);
  const offsetX = Number.isFinite(clientX) ? Math.max(0, Math.min(clientX - Number(rect.left || 0), width)) : width / 2;
  const offsetY = Number.isFinite(clientY) ? Math.max(0, Math.min(clientY - Number(rect.top || 0), height)) : height / 2;
  dataTransfer.setDragImage(clone, offsetX, offsetY);

  const removeClone = () => clone.remove();
  if (typeof window !== "undefined" && typeof window.requestAnimationFrame === "function") {
    window.requestAnimationFrame(removeClone);
    return;
  }

  if (typeof window !== "undefined") {
    window.setTimeout(removeClone, 0);
    return;
  }

  removeClone();
}

function parseIsoDateKey(value) {
  const match = String(value || "").slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;

  const [, year, month, day] = match;
  return new Date(Number(year), Number(month) - 1, Number(day));
}

function getDaySpanCount(startValue, endValue) {
  const start = parseIsoDateKey(startValue);
  const end = parseIsoDateKey(endValue || startValue);
  if (!start || !end || end < start) return 1;

  return Math.max(1, Math.round((end.getTime() - start.getTime()) / MILLISECONDS_PER_DAY) + 1);
}

function getDraggedEntryDayCount(entry) {
  if (entry?.entryType === "user_holiday" || entry?.holidayTypeId || entry?.userHolidayTypeId) {
    return getDaySpanCount(entry.startDate || entry.dateKey, entry.endDate || entry.startDate || entry.dateKey);
  }

  if (entry?.entryType !== "absence") return 1;
  return getDaySpanCount(entry.start || entry.dateKey, entry.end || entry.start || entry.dateKey);
}

function getDraggedEntryKey(entry) {
  const id = String(entry?.id || "").trim();
  if (!id) return "";

  if (isDemandDragEntry(entry)) return `demand:${id}`;

  if (entry?.entryType === "user_holiday" || entry?.holidayTypeId || entry?.userHolidayTypeId) {
    return `user-holiday:${id}`;
  }

  if (entry?.entryType === "absence") return `absence:${id}`;
  return `shift:${id}`;
}

function getSpanningEntryWidth(spanDays) {
  const safeSpanDays = Math.max(1, Number(spanDays) || 1);
  const gridLineCompensation = safeSpanDays - 1;
  if (!gridLineCompensation) return "calc(100% - 6px)";

  return `calc(${safeSpanDays * 100}% + ${gridLineCompensation}px - 6px)`;
}

function getSpanDragDayOffset(event, spanDays) {
  const safeSpanDays = Math.max(1, Number(spanDays) || 1);
  if (safeSpanDays === 1) return 0;

  const rect = event?.currentTarget?.getBoundingClientRect?.();
  const width = Number(rect?.width || 0);
  const left = Number(rect?.left || 0);
  const clientX = Number(event?.clientX || 0);
  if (!width || !Number.isFinite(clientX)) return 0;

  const relativeX = Math.max(0, Math.min(clientX - left, width - 1));
  return Math.max(0, Math.min(safeSpanDays - 1, Math.floor((relativeX / width) * safeSpanDays)));
}

function getAbsenceSpanKey(shift) {
  const id = String(shift?.id || "").trim();
  if (id) return `absence:${id}`;

  return [
    "absence",
    shift?.employeeId || "",
    shift?.department || "",
    shift?.title || "",
    shift?.start || "",
    shift?.end || "",
  ].join(":");
}

function getUserHolidaySpanKey(holiday) {
  const id = String(holiday?.id || "").trim();
  if (id) return `user-holiday:${id}`;

  return [
    "user-holiday",
    holiday?.userId || "",
    holiday?.holidayTypeId || holiday?.userHolidayTypeId || "",
    holiday?.holidayTypeName || "",
    holiday?.startDate || "",
    holiday?.endDate || "",
  ].join(":");
}

function getSpanningEntryKey(type, entry) {
  return type === "user-holiday" ? getUserHolidaySpanKey(entry) : getAbsenceSpanKey(entry);
}

function getSpanningEntryStartDateKey(type, entry) {
  if (type === "user-holiday") return String(entry?.startDate || entry?.dateKey || "").slice(0, 10);
  return String(entry?.start || entry?.dateKey || "").slice(0, 10);
}

function getSpanningEntryEndDateKey(type, entry) {
  if (type === "user-holiday") {
    return String(entry?.endDate || entry?.startDate || entry?.dateKey || "").slice(0, 10);
  }

  return String(entry?.end || entry?.start || entry?.dateKey || "").slice(0, 10);
}

function isSingleDayEntryOnDay(type, entry, dayKey) {
  const startDateKey = getSpanningEntryStartDateKey(type, entry);
  const endDateKey = getSpanningEntryEndDateKey(type, entry);
  return Boolean(startDateKey && endDateKey && startDateKey === dayKey && endDateKey === dayKey);
}

function registerSpanningCandidate(candidates, key, entry, dayIndex, type) {
  if (!key) return;
  const current = candidates.get(key) || {
    key,
    type,
    entry,
    startIndex: dayIndex,
    endIndex: dayIndex,
  };

  candidates.set(key, {
    ...current,
    startIndex: Math.min(current.startIndex, dayIndex),
    endIndex: Math.max(current.endIndex, dayIndex),
  });
}

function assignSpanningEntryLanes(entries) {
  const laneEnds = [];

  return entries.map((entry) => {
    const laneIndex = laneEnds.findIndex((endIndex) => endIndex < entry.startIndex);
    const lane = laneIndex === -1 ? laneEnds.length : laneIndex;
    laneEnds[lane] = entry.endIndex;
    return { ...entry, lane };
  });
}

function compareSpanningEntries(left, right) {
  if (left.startIndex !== right.startIndex) return left.startIndex - right.startIndex;
  if (left.endIndex !== right.endIndex) return right.endIndex - left.endIndex;
  return left.type.localeCompare(right.type);
}

function buildRowEntryRanges({ row, rowMode, days, shiftsByCell, userHolidaysByCell }) {
  const candidates = new Map();

  days.forEach((day, dayIndex) => {
    const cellKey = `${row.id}::${day.key}`;
    const cellShifts = shiftsByCell[cellKey] || [];

    cellShifts.forEach((shift) => {
      if (shift?.entryType !== "absence") return;
      registerSpanningCandidate(candidates, getAbsenceSpanKey(shift), shift, dayIndex, "absence");
    });

    if (rowMode !== "employee") return;

    const cellUserHolidays = userHolidaysByCell[cellKey] || [];
    cellUserHolidays.forEach((holiday) => {
      registerSpanningCandidate(candidates, getUserHolidaySpanKey(holiday), holiday, dayIndex, "user-holiday");
    });
  });

  return Array.from(candidates.values()).sort(compareSpanningEntries);
}

function buildSpanningEntriesFromRanges(ranges) {
  return assignSpanningEntryLanes(ranges.filter((entry) => entry.endIndex > entry.startIndex));
}

function doSpanningRangesOverlap(left, right) {
  return left.startIndex <= right.endIndex && right.startIndex <= left.endIndex;
}

function buildResizeBounds(dayCount) {
  return {
    minStartIndex: 0,
    maxEndIndex: Math.max(0, Number(dayCount || 1) - 1),
  };
}

function applyResizeBounds(range, bounds) {
  if (!bounds) return range;

  return {
    startIndex: Math.max(range.startIndex, bounds.minStartIndex),
    endIndex: Math.min(range.endIndex, bounds.maxEndIndex),
  };
}

function getResizePreviewRange(resize, targetDayIndex) {
  const numericDayIndex = Number(targetDayIndex);
  const safeDayIndex = Number.isFinite(numericDayIndex) ? numericDayIndex : resize.originalStartIndex;
  const maxDayIndex = Math.max(0, resize.dayCount - 1);
  const boundedDayIndex = Math.max(0, Math.min(safeDayIndex, maxDayIndex));

  if (resize.edge === "start") {
    return applyResizeBounds(
      {
        startIndex: Math.min(boundedDayIndex, resize.originalEndIndex),
        endIndex: resize.originalEndIndex,
      },
      resize.bounds
    );
  }

  return applyResizeBounds(
    {
      startIndex: resize.originalStartIndex,
      endIndex: Math.max(boundedDayIndex, resize.originalStartIndex),
    },
    resize.bounds
  );
}

function applySpanResizePreview(entries, ranges, resize, rowId) {
  if (!resize || String(resize.rowId) !== String(rowId)) return entries;

  let didApplyPreview = false;
  const previewEntries = entries.map((entry) => {
    if (entry.key !== resize.key || entry.type !== resize.type) return entry;

    didApplyPreview = true;
    return {
      ...entry,
      startIndex: resize.previewStartIndex,
      endIndex: resize.previewEndIndex,
    };
  });

  if (!didApplyPreview) {
    previewEntries.push({
      key: resize.key,
      type: resize.type,
      entry: resize.entry,
      startIndex: resize.previewStartIndex,
      endIndex: resize.previewEndIndex,
    });
  }

  const previewRange = {
    key: resize.key,
    type: resize.type,
    startIndex: resize.previewStartIndex,
    endIndex: resize.previewEndIndex,
  };
  const entriesByKey = new Map(previewEntries.map((entry) => [`${entry.type}:${entry.key}`, entry]));

  ranges.forEach((range) => {
    if (!range || (range.key === resize.key && range.type === resize.type)) return;
    if (range.endIndex > range.startIndex || !doSpanningRangesOverlap(range, previewRange)) return;
    entriesByKey.set(`${range.type}:${range.key}`, range);
  });

  return assignSpanningEntryLanes([...entriesByKey.values()].sort(compareSpanningEntries));
}

function buildSpanningEntryLookup(entries, dayCount) {
  return entries.reduce(
    (lookup, entry) => {
      if (entry.type === "absence") lookup.absenceKeys.add(entry.key);
      if (entry.type === "user-holiday") lookup.holidayKeys.add(entry.key);

      if (!lookup.byStartIndex[entry.startIndex]) lookup.byStartIndex[entry.startIndex] = [];
      lookup.byStartIndex[entry.startIndex].push(entry);

      for (let index = entry.startIndex; index <= entry.endIndex && index < dayCount; index += 1) {
        lookup.coverCounts[index] = Math.max(lookup.coverCounts[index] || 0, entry.lane + 1);
      }

      return lookup;
    },
    {
      absenceKeys: new Set(),
      holidayKeys: new Set(),
      byStartIndex: {},
      coverCounts: Array(dayCount).fill(0),
    }
  );
}

function getTopEntrySpace(count) {
  if (!count) return "";
  return `${SPANNING_ENTRY_TOP + count * SPANNING_ENTRY_HEIGHT + (count - 1) * SPANNING_ENTRY_GAP + SPANNING_ENTRY_GAP}px`;
}

function getDragTargetStartIndex({ row, days, dragTargetCellKey, dragAnchorDayOffset = 0 }) {
  const rowPrefix = `${row?.id || ""}::`;
  if (!dragTargetCellKey || !rowPrefix || !String(dragTargetCellKey).startsWith(rowPrefix)) return -1;

  const targetDayKey = String(dragTargetCellKey).slice(rowPrefix.length);
  const targetIndex = days.findIndex((day) => day.key === targetDayKey);
  if (targetIndex < 0) return -1;

  const safeOffset = Math.max(0, Math.floor(Number(dragAnchorDayOffset) || 0));
  return Math.max(0, targetIndex - safeOffset);
}

function isDayInsideDragTarget({ draggedEntry, dragTargetStartIndex, dayIndex, dayKey }) {
  if (!draggedEntry || dragTargetStartIndex < 0) return false;

  // Bedarfs-Einträge gelten pro Datum: nur die gehoverte Zelle desselben Tages ist Ziel.
  if (isDemandDragEntry(draggedEntry)) {
    return dayIndex === dragTargetStartIndex && String(dayKey || "") === String(draggedEntry.dateKey || "");
  }

  const dayCount = getDraggedEntryDayCount(draggedEntry);
  return dayIndex >= dragTargetStartIndex && dayIndex < dragTargetStartIndex + dayCount;
}

// Der Auslastungsring kommt aus dem DS (AppPersonBadge); hier wird nur die
// Grad-Angabe des Grids auf den 0..1-Anteil des DS umgerechnet.
function WorkloadRing({ workload, employeeName, onStatsOpen }) {
  return (
    <AppPersonBadge
      className={`spv2-grid-employee-progress is-${workload.tone}`}
      name={employeeName}
      size="md"
      showProgress
      progress={degreesToPercent(workload.progressDegrees) / 100}
      overage={degreesToPercent(workload.overageDegrees) / 100}
      progressTone={workload.tone}
      onClick={onStatsOpen}
      ariaLabel={`Statistik für ${employeeName || "Mitarbeitende"} öffnen`}
    />
  );
}

function handleKeyboardActivation(event, callback) {
  if (!callback) return;
  if (event.key !== "Enter" && event.key !== " ") return;

  event.preventDefault();
  event.stopPropagation();
  callback(event);
}

function buildCellContext(row, day, rowMode) {
  return { row, day, rowMode };
}

function formatCompactTimeRangeLabel(value) {
  return String(value || "").replace(/(\d{1,2}:\d{2})\s*-\s*(\d{1,2}:\d{2})/g, "$1-$2");
}

function buildShiftTooltipTitle(shift, metaLabel, { showEmployee = false } = {}) {
  const timeLabel = formatCompactTimeRangeLabel(shift.timeLabel) || "Zeit offen";
  const parts = [
    `${shift.isDraft ? "Nicht freigegeben · " : ""}${timeLabel} ${shift.title}`,
  ];

  // Im Abteilungs-Modus steht in der Kachel nur die Person, und ihr Name wird
  // im Monat gekürzt. Der Tooltip ist dann die einzige Stelle, an der der volle
  // Name und die weggefallene Schichtbezeichnung noch zu lesen sind.
  if (showEmployee && shift.employeeName) {
    parts.unshift(shift.employeeName);
  }

  if (shift.department && shift.department !== shift.title) {
    parts.push(shift.department);
  }

  if (metaLabel && metaLabel !== shift.department && metaLabel !== shift.departmentShortName) {
    parts.push(metaLabel);
  }

  return parts.join(" · ");
}

function getShiftTitleLabel(shift, compactLabels) {
  return compactLabels ? shift.titleShort || shift.title : shift.title;
}

function getShiftDepartmentLabel(shift, compactLabels) {
  return compactLabels ? shift.departmentShortName || shift.department : shift.department;
}

function getDisplayTitleLabel(shift, compactLabels, displayParts) {
  if (shift.entryType === "absence") return getShiftTitleLabel(shift, compactLabels);
  return displayParts.showShift ? getShiftTitleLabel(shift, compactLabels) : "";
}

function getDisplayDepartmentLabel(shift, compactLabels, displayParts) {
  if (shift.entryType === "absence" || !displayParts.showDepartment) return "";
  return getShiftDepartmentLabel(shift, compactLabels) || getShiftTitleLabel(shift, compactLabels);
}

/*
  Im Abteilungs-Modus sagt die Kachel, WER arbeitet — das ist dort die einzige
  Angabe, die nicht schon links am Zeilenkopf steht. Im Monat die Kurzform
  ("A. Meier"), in der Woche der volle Name; die Spalten sind dort drei- bis
  viermal so breit.
*/
function getEmployeeChipLabel(shift, compactLabels) {
  if (!compactLabels) return shift.employeeName || "";
  return shift.employeeShortName || shortenFullName(shift.employeeName) || "";
}

function ShiftChip({
  shift,
  compactLabels = false,
  shiftDisplayMode = SHIFT_DISPLAY_MODES.TIME_DEPARTMENT,
  selected,
  copied,
  dragging,
  releasePreviewActive = false,
  releasePreviewTarget = false,
  canResizeStart = false,
  canResizeEnd = false,
  onSelect,
  onOpen,
  onContextMenu,
  onDragStart,
  onDragEnd,
  onResizeStart,
}) {
  const normalizedMode = normalizeShiftDisplayMode(shiftDisplayMode);
  const displayParts = getShiftDisplayParts(normalizedMode);

  return (
    <ShiftChipContent
      shift={shift}
      titleLabel={getDisplayTitleLabel(shift, compactLabels, displayParts)}
      metaLabel={getDisplayDepartmentLabel(shift, compactLabels, displayParts)}
      selected={selected}
      copied={copied}
      dragging={dragging}
      releasePreviewActive={releasePreviewActive}
      releasePreviewTarget={releasePreviewTarget}
      canResizeStart={canResizeStart}
      canResizeEnd={canResizeEnd}
      onSelect={onSelect}
      onOpen={onOpen}
      onContextMenu={onContextMenu}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onResizeStart={onResizeStart}
    />
  );
}

function DepartmentShiftChip({
  shift,
  compactLabels = false,
  selected,
  copied,
  dragging,
  releasePreviewActive = false,
  releasePreviewTarget = false,
  canResizeStart = false,
  canResizeEnd = false,
  onSelect,
  onOpen,
  onContextMenu,
  onDragStart,
  onDragEnd,
  onResizeStart,
}) {
  /*
    Die Einstellung "Zeit + Abteilung" / "Zeit + Schichtname" wird hier bewusst
    nicht ausgewertet. Die Abteilung stünde doppelt (sie ist der Zeilenname),
    und für den Schichtnamen ist kein Platz mehr, sobald die Person die Zeile
    belegt. Der Schichtname bleibt im Tooltip lesbar. Im Mitarbeitenden-Modus
    wirkt die Einstellung unverändert.
  */
  return (
    <ShiftChipContent
      shift={shift}
      // Als titleLabel, damit auch der Absenz-Zweig die Person zeigt statt
      // nochmals "Ferien" — den Titel trägt dort schon der Zeilenkopf.
      titleLabel={getEmployeeChipLabel(shift, compactLabels)}
      metaLabel=""
      showEmployeeInTooltip
      selected={selected}
      copied={copied}
      dragging={dragging}
      releasePreviewActive={releasePreviewActive}
      releasePreviewTarget={releasePreviewTarget}
      canResizeStart={canResizeStart}
      canResizeEnd={canResizeEnd}
      onSelect={onSelect}
      onOpen={onOpen}
      onContextMenu={onContextMenu}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onResizeStart={onResizeStart}
    />
  );
}

function ShiftChipContent({
  shift,
  titleLabel,
  metaLabel,
  showEmployeeInTooltip = false,
  selected,
  copied,
  dragging,
  releasePreviewActive = false,
  releasePreviewTarget = false,
  canResizeStart = false,
  canResizeEnd = false,
  onSelect,
  onOpen,
  onContextMenu,
  onDragStart,
  onDragEnd,
  onResizeStart,
}) {
  const tooltipTitle = buildShiftTooltipTitle(shift, metaLabel, { showEmployee: showEmployeeInTooltip });

  const isInteractive = Boolean(onSelect || onOpen || onContextMenu);
  const isAbsence = shift.entryType === "absence";
  const isReleasePreviewDimmed = releasePreviewActive && !releasePreviewTarget;
  const compactTimeLabel = formatCompactTimeRangeLabel(shift.timeLabel);
  const primaryLabel = titleLabel || metaLabel;
  const primaryLabelClassName = titleLabel ? "spv2-shift-title" : "spv2-shift-meta";

  const handleSelect = (event) => {
    event.stopPropagation();
    onSelect?.(event);
  };

  const handleOpen = (event) => {
    event.preventDefault();
    event.stopPropagation();
    onOpen?.(event);
  };

  const handleContextMenu = (event) => {
    event.stopPropagation();
    onContextMenu?.(event);
  };

  const handleDragStart = (event) => {
    event.stopPropagation();
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", String(shift.id || ""));
    prepareEntryDragImage(event);
    onDragStart?.(event);
  };

  const handleDragEnd = (event) => {
    event.stopPropagation();
    onDragEnd?.(event);
  };
  const handleResizeStart = (edge, event) => {
    event.preventDefault();
    event.stopPropagation();
    onResizeStart?.(edge, event);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      handleOpen(event);
      return;
    }

    if (event.key === " ") {
      event.preventDefault();
      handleSelect(event);
    }
  };

  return (
    <ShiftplanTooltip title={tooltipTitle}>
      <Box
        className={`spv2-shift-chip${isAbsence ? " spv2-shift-chip--absence" : ""}${
          shift.isDraft ? " spv2-shift-chip--draft" : ""
        }${
          releasePreviewTarget ? " is-release-preview-target" : ""
        }${
          isReleasePreviewDimmed ? " is-release-preview-dimmed" : ""
        }${
          isInteractive ? " is-interactive" : ""
        }${selected ? " is-selected" : ""}${copied ? " is-copied" : ""}${dragging ? " is-dragging" : ""}`}
        style={{ "--spv2-shift-color": shift.color }}
        role={isInteractive ? "button" : undefined}
        tabIndex={isInteractive ? 0 : undefined}
        draggable={Boolean(onDragStart)}
        aria-grabbed={dragging ? "true" : undefined}
        onMouseDown={isInteractive ? stopGridPointerSelection : undefined}
        onClick={isInteractive ? handleSelect : undefined}
        onDoubleClick={isInteractive ? handleOpen : undefined}
        onContextMenu={isInteractive ? handleContextMenu : undefined}
        onDragStart={onDragStart ? handleDragStart : undefined}
        onDragEnd={onDragEnd ? handleDragEnd : undefined}
        onKeyDown={isInteractive ? handleKeyDown : undefined}
      >
        {isAbsence && canResizeStart ? (
          <Box
            component="span"
            className="spv2-grid-span-resize-handle is-start"
            aria-label="Startdatum anpassen"
            onMouseDown={(event) => handleResizeStart("start", event)}
            onClick={stopGridPointerSelection}
            onDoubleClick={stopGridPointerSelection}
            onContextMenu={stopGridPointerSelection}
          />
        ) : null}
        {isAbsence ? (
          <Box component="span" className="spv2-shift-absence-content">
            <EventBusyOutlinedIcon fontSize="inherit" />
            <Box component="span" className="spv2-shift-title">
              {titleLabel || shift.title}
            </Box>
            {compactTimeLabel && compactTimeLabel !== "Ganztag" ? (
              <Box component="span" className="spv2-shift-time">
                {compactTimeLabel}
              </Box>
            ) : null}
          </Box>
        ) : (
          <>
            {primaryLabel ? (
              <Box component="span" className={primaryLabelClassName}>
                {primaryLabel}
              </Box>
            ) : null}
            <Box component="span" className="spv2-shift-time">
              {compactTimeLabel || "Zeit offen"}
            </Box>
          </>
        )}
        {shift.isDraft ? (
          // Gleiches Freigabe-Icon wie am "X offen"-Button in der Toolbar.
          <Box component="span" className="spv2-shift-draft-badge" aria-hidden="true">
            <PendingActionsOutlinedIcon fontSize="inherit" />
          </Box>
        ) : null}
        {isAbsence && canResizeEnd ? (
          <Box
            component="span"
            className="spv2-grid-span-resize-handle is-end"
            aria-label="Enddatum anpassen"
            onMouseDown={(event) => handleResizeStart("end", event)}
            onClick={stopGridPointerSelection}
            onDoubleClick={stopGridPointerSelection}
            onContextMenu={stopGridPointerSelection}
          />
        ) : null}
      </Box>
    </ShiftplanTooltip>
  );
}

function DepartmentAvatar() {
  return (
    <Box className="spv2-grid-department-avatar" aria-hidden="true">
      <IconDepartment fontSize="small" />
    </Box>
  );
}

function EmployeeRowContent({ row, workload, onStatsOpen }) {
  return (
    <Box className="spv2-grid-employee-row">
      <WorkloadRing workload={workload} employeeName={row.name} onStatsOpen={onStatsOpen} />
      <Box className="spv2-grid-employee-copy">
        <Typography className="spv2-grid-employee-name">{row.name}</Typography>
        <Box className="spv2-grid-employee-meta-line">
          <Typography className="spv2-grid-employee-hours">{workload.label}</Typography>
          {workload.vacationDays > 0 ? (
            <ShiftplanTooltip title={workload.vacationLabel}>
              <Box
                className="spv2-grid-employee-vacation has-vacation"
                aria-label={workload.vacationLabel}
              >
                <IconHoliday fontSize="inherit" />
                <Box component="span">{workload.vacationDays}</Box>
              </Box>
            </ShiftplanTooltip>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
}

/*
  Mitarbeitenden-Ansicht: eigene Zeile mit gefülltem Avatar und Fortschritts-
  balken, Kolleginnen und Kollegen schlicht ohne Auslastungsring.
*/
function EmployeeViewRowContent({ row, workload, isOwn }) {
  const progress = Math.min(100, Math.max(0, degreesToPercent(workload.progressDegrees)));
  return (
    <Box className={`spv2-grid-employee-row${isOwn ? " is-own" : ""}`}>
      <AppPersonBadge
        className={`spv2-grid-employee-avatar${isOwn ? " spv2-grid-employee-avatar--own" : ""}`}
        name={row.name}
        size="md"
        tone="neutral"
      />
      <Box className="spv2-grid-employee-copy">
        <Typography className="spv2-grid-employee-name">{row.name}</Typography>
        <Typography className="spv2-grid-employee-hours">{workload.label}</Typography>
        {isOwn ? (
          <Box className="spv2-grid-employee-progress-bar" role="presentation">
            <Box component="span" style={{ width: `${progress}%` }} />
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}

function DepartmentRowContent({ row }) {
  return (
    <Box className="spv2-grid-employee-row">
      <DepartmentAvatar />
      <Box className="spv2-grid-employee-copy">
        <Typography className="spv2-grid-employee-name">{row.name}</Typography>
        <Typography className="spv2-grid-department-meta">{row.metaLabel}</Typography>
      </Box>
    </Box>
  );
}

function AvailabilityMarker({ availability }) {
  if (!availability?.label) return null;

  return (
    <ShiftplanTooltip title={availability.tooltip || availability.label}>
      <Box
        className={`spv2-grid-availability-marker is-${availability.status} is-${availability.kind || "availability"}`}
        aria-label={availability.tooltip}
      >
        {availability.label}
      </Box>
    </ShiftplanTooltip>
  );
}

function UserHolidayMarker({
  holidays,
  selectedHolidayId = "",
  copiedHolidayId = "",
  draggedEntryKey = "",
  releasePreviewActive = false,
  onSelect,
  onOpen,
  onContextMenu,
  onDragStart,
  onDragEnd,
  getResizeProps,
}) {
  if (!Array.isArray(holidays) || !holidays.length) return null;

  const isInteractive = Boolean(onSelect || onOpen || onContextMenu);
  const handleOpen = (event, holiday) => {
    event.preventDefault();
    event.stopPropagation();
    onOpen?.(holiday);
  };
  const handleSelect = (event, holiday) => {
    event.preventDefault();
    event.stopPropagation();
    onSelect?.(holiday);
  };
  const handleContextMenu = (event, holiday) => {
    if (!onContextMenu) return;
    event.preventDefault();
    event.stopPropagation();
    onContextMenu(event, holiday);
  };
  const handleResizeStart = (event, resizeProps, edge) => {
    event.preventDefault();
    event.stopPropagation();
    resizeProps?.onResizeStart?.(edge, event);
  };
  const handleDragStart = (event, holiday) => {
    event.stopPropagation();
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setData("text/plain", String(holiday.id || ""));
      prepareEntryDragImage(event);
    }
    onDragStart?.(holiday, { dragDayOffset: 0 });
  };
  const handleDragEnd = (event, holiday) => {
    event.stopPropagation();
    onDragEnd?.(holiday);
  };

  return (
    <Box className="spv2-grid-user-holiday-stack">
      {holidays.map((holiday, index) => {
        const label = holiday.holidayTypeName || "Ferien";
        const isSelected = Boolean(selectedHolidayId) && String(holiday.id) === String(selectedHolidayId);
        const isCopied = Boolean(copiedHolidayId) && String(holiday.id) === String(copiedHolidayId);
        const isDragging = Boolean(draggedEntryKey) && getDraggedEntryKey(holiday) === draggedEntryKey;
        const resizeProps = getResizeProps?.(holiday) || {};

        return (
          <ShiftplanTooltip title={label} key={holiday.id || `${label}-${index}`}>
            <Box
              className={`spv2-grid-user-holiday-marker${isInteractive ? " is-interactive" : ""}${
                isSelected ? " is-selected" : ""
              }${isCopied ? " is-copied" : ""}${isDragging ? " is-dragging" : ""}${
                releasePreviewActive ? " is-release-preview-dimmed" : ""
              }`}
              role={isInteractive ? "button" : undefined}
              tabIndex={isInteractive ? 0 : undefined}
              draggable={Boolean(onDragStart)}
              aria-label={`Ferien bearbeiten: ${label}`}
              onMouseDown={isInteractive ? stopGridPointerSelection : undefined}
              onClick={onSelect ? (event) => handleSelect(event, holiday) : undefined}
              onDoubleClick={onOpen ? (event) => handleOpen(event, holiday) : undefined}
              onContextMenu={onContextMenu ? (event) => handleContextMenu(event, holiday) : undefined}
              onDragStart={onDragStart ? (event) => handleDragStart(event, holiday) : undefined}
              onDragEnd={onDragEnd ? (event) => handleDragEnd(event, holiday) : undefined}
              onKeyDown={
                isInteractive
                  ? (event) => {
                      if (event.key === "Enter" && onOpen) {
                        handleOpen(event, holiday);
                        return;
                      }

                      if ((event.key === " " || event.key === "Spacebar") && onSelect) {
                        handleSelect(event, holiday);
                      }
                    }
                  : undefined
              }
            >
              {resizeProps.canResizeStart ? (
                <Box
                  component="span"
                  className="spv2-grid-span-resize-handle is-start"
                  aria-label="Startdatum anpassen"
                  onMouseDown={(event) => handleResizeStart(event, resizeProps, "start")}
                  onClick={stopGridPointerSelection}
                  onDoubleClick={stopGridPointerSelection}
                  onContextMenu={stopGridPointerSelection}
                />
              ) : null}
              <Box component="span" className="spv2-grid-entry-content">
                <IconHoliday fontSize="inherit" />
                <Box component="span" className="spv2-grid-entry-label">
                  {label}
                </Box>
              </Box>
              {resizeProps.canResizeEnd ? (
                <Box
                  component="span"
                  className="spv2-grid-span-resize-handle is-end"
                  aria-label="Enddatum anpassen"
                  onMouseDown={(event) => handleResizeStart(event, resizeProps, "end")}
                  onClick={stopGridPointerSelection}
                  onDoubleClick={stopGridPointerSelection}
                  onContextMenu={stopGridPointerSelection}
                />
              ) : null}
            </Box>
          </ShiftplanTooltip>
        );
      })}
    </Box>
  );
}

function SpanningEntryMarker({
  entry,
  type,
  spanDays,
  lane,
  showEmployee = false,
  selected = false,
  copied = false,
  dragging = false,
  resizing = false,
  releasePreviewActive = false,
  releasePreviewTarget = false,
  canResizeStart = false,
  canResizeEnd = false,
  onSelect,
  onOpen,
  onContextMenu,
  onDragStart,
  onDragEnd,
  onResizeStart,
}) {
  const isUserHoliday = type === "user-holiday";
  const Icon = isUserHoliday ? IconHoliday : EventBusyOutlinedIcon;
  const entryLabel = isUserHoliday ? entry.holidayTypeName || "Ferien" : entry.title || entry.department || "Absenz";
  /*
    Im Abteilungs-Modus heisst die Zeile bereits "Ferien" oder "Krankheit" —
    Absenzen ohne Abteilung landen dort, weil der Mapper den Absenztitel als
    Abteilung einsetzt (api/mappers.js). Der Balken wiederholt das heute nur.
    Er zeigt stattdessen die Person, und zwar immer mit vollem Namen: ein
    Balken umfasst mindestens zwei Tage, also mindestens 130px auch im Monat —
    der längste gemessene Name braucht 99px.
  */
  const label = showEmployee ? entry.employeeName || entryLabel : entryLabel;
  const tooltipLabel = label === entryLabel ? label : `${label} · ${entryLabel}`;
  const isInteractive = Boolean(onSelect || onOpen || onContextMenu);
  const isReleasePreviewDimmed = releasePreviewActive && !releasePreviewTarget;
  const style = {
    "--spv2-span-entry-top": `${SPANNING_ENTRY_TOP + lane * (SPANNING_ENTRY_HEIGHT + SPANNING_ENTRY_GAP)}px`,
    "--spv2-span-entry-width": getSpanningEntryWidth(spanDays),
    "--spv2-span-entry-color": isUserHoliday
      ? "var(--spv2-vacation)"
      : entry.color || "var(--ds-color-category-08-default)",
  };

  const handleSelect = (event) => {
    event.stopPropagation();
    onSelect?.(event);
  };
  const handleOpen = (event) => {
    event.preventDefault();
    event.stopPropagation();
    onOpen?.(event);
  };
  const handleContextMenu = (event) => {
    event.stopPropagation();
    onContextMenu?.(event);
  };
  const handleDragStart = (event) => {
    event.stopPropagation();
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setData("text/plain", String(entry.id || ""));
      prepareEntryDragImage(event);
    }
    onDragStart?.(event, { dragDayOffset: getSpanDragDayOffset(event, spanDays) });
  };
  const handleDragEnd = (event) => {
    event.stopPropagation();
    onDragEnd?.(event);
  };
  const handleResizeStart = (edge, event) => {
    event.preventDefault();
    event.stopPropagation();
    onResizeStart?.(edge, event);
  };
  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      handleOpen(event);
      return;
    }

    if (event.key === " ") {
      event.preventDefault();
      handleSelect(event);
    }
  };

  return (
    <ShiftplanTooltip title={tooltipLabel}>
      <Box
        className={`spv2-grid-span-entry ${isUserHoliday ? "is-user-holiday" : "is-absence"}${
          isInteractive ? " is-interactive" : ""
        }${selected ? " is-selected" : ""}${copied ? " is-copied" : ""}${dragging ? " is-dragging" : ""}${
          resizing ? " is-resizing" : ""
        }${isReleasePreviewDimmed ? " is-release-preview-dimmed" : ""
        }`}
        style={style}
        role={isInteractive ? "button" : undefined}
        tabIndex={isInteractive ? 0 : undefined}
        draggable={Boolean(onDragStart)}
        aria-label={`${isUserHoliday ? "Ferien" : "Absenz"} bearbeiten: ${label}`}
        onMouseDown={isInteractive ? stopGridPointerSelection : undefined}
        onClick={isInteractive ? handleSelect : undefined}
        onDoubleClick={isInteractive ? handleOpen : undefined}
        onContextMenu={isInteractive ? handleContextMenu : undefined}
        onDragStart={onDragStart ? handleDragStart : undefined}
        onDragEnd={onDragEnd ? handleDragEnd : undefined}
        onKeyDown={isInteractive ? handleKeyDown : undefined}
      >
        {canResizeStart ? (
          <Box
            component="span"
            className="spv2-grid-span-resize-handle is-start"
            aria-label="Startdatum anpassen"
            onMouseDown={(event) => handleResizeStart("start", event)}
            onClick={stopGridPointerSelection}
            onDoubleClick={stopGridPointerSelection}
            onContextMenu={stopGridPointerSelection}
          />
        ) : null}
        <Box component="span" className="spv2-grid-entry-content">
          <Icon fontSize="inherit" />
          <Box component="span" className="spv2-grid-entry-label">
            {label}
          </Box>
        </Box>
        {canResizeEnd ? (
          <Box
            component="span"
            className="spv2-grid-span-resize-handle is-end"
            aria-label="Enddatum anpassen"
            onMouseDown={(event) => handleResizeStart("end", event)}
            onClick={stopGridPointerSelection}
            onDoubleClick={stopGridPointerSelection}
            onContextMenu={stopGridPointerSelection}
          />
        ) : null}
      </Box>
    </ShiftplanTooltip>
  );
}

export default function ShiftplanMonthGrid({
  viewMode = "month",
  rowMode = "employee",
  days,
  rows,
  shiftsByCell,
  availabilityByCell = {},
  userHolidaysByCell = {},
  showAvailability = false,
  workloadByEmployeeId = {},
  publicHolidayNamesByDate = {},
  dayCommentsByDate = {},
  onSaveDayComment,
  /*
    Mitarbeitenden-Ansicht: die eigene Zeile steht unter "Dein Plan", alle
    anderen unter "Team" ({ ownLabel, teamLabel }). Ohne employeeSections
    (Planung) bleibt das Raster wie es ist.
  */
  currentEmployeeId = "",
  employeeSections = null,
  selectedShiftId = "",
  selectedShiftIds = null,
  selectedUserHolidayId = "",
  selectedCellKeys = new Set(),
  copiedTimetableEntryId = "",
  copiedTimetableEntryIds = null,
  copiedUserHolidayId = "",
  draggedEntry = null,
  dragTargetCellKey = "",
  dragAnchorDayOffset = 0,
  releasePreviewShiftIds = null,
  conflictPreviewShiftIds = null,
  /*
    Hebt AUSSCHLIESSLICH die Einträge aus releasePreviewShiftIds hervor und
    blasst alles andere ab — auch wenn die Menge leer ist. Ohne das Flag
    greift die Hervorhebung erst ab einem Treffer; die Solver-Vorschau muss
    aber auch einen Lauf ohne neue Einsätze korrekt zeigen (dann ist eben
    alles Bestand und damit blass).
  */
  dimUnhighlighted = false,
  focusCell = null,
  shiftDisplayMode = SHIFT_DISPLAY_MODES.TIME_DEPARTMENT,
  demandAvailable = false,
  demandVisible = true,
  demandByDay = {},
  demandExpanded = false,
  demandCompact = false,
  demandTotalMissing = 0,
  demandTotalOver = 0,
  canDragDemand = false,
  onDemandVisibleChange,
  onDemandToggleExpanded,
  onDemandDragStart,
  onDemandDragEnd,
  onShiftDropOnDemand,
  onCellSelect,
  onCellRangeSelect,
  onCellDoubleClick,
  onCellContextMenu,
  onCellDragTarget,
  onCellDropEntry,
  onShiftSelect,
  onShiftDoubleClick,
  onShiftContextMenu,
  onEntryDragStart,
  onEntryDragEnd,
  onEmployeeStatsOpen,
  onUserHolidaySelect,
  onUserHolidayDoubleClick,
  onUserHolidayContextMenu,
  onSpanResizeCommit,
}) {
  const gridScrollerRef = useRef(null);
  const hScrollbarRef = useRef(null);
  const vScrollbarRef = useRef(null);
  const isSelectingRef = useRef(false);
  const selectionAnchorRef = useRef(null);
  const lastSelectionAnchorRef = useRef(null);
  const spanResizeRef = useRef(null);
  const [expandedShiftCellKeys, setExpandedShiftCellKeys] = useState(() => new Set());
  const [spanResize, setSpanResize] = useState(null);
  const [dayCommentTarget, setDayCommentTarget] = useState(null);
  const teamCount = employeeSections
    ? rows.filter((row) => String(row.id) !== String(currentEmployeeId)).length
    : 0;
  // Tagesnotizen bearbeitet nur, wer speichern darf (die Mitarbeitenden-Ansicht reicht keinen Handler).
  const canEditDayComments = Boolean(onSaveDayComment);
  // Konflikt-Hover und Freigabe-Vorschau nutzen dieselbe Hervorhebung; der
  // Konflikt-Hover hat Vorrang, wenn beide gleichzeitig aktiv wären.
  const previewShiftIds =
    conflictPreviewShiftIds instanceof Set && conflictPreviewShiftIds.size > 0
      ? conflictPreviewShiftIds
      : releasePreviewShiftIds;
  const hasReleasePreview =
    previewShiftIds instanceof Set && (previewShiftIds.size > 0 || dimUnhighlighted);
  const normalizedShiftDisplayMode = normalizeShiftDisplayMode(shiftDisplayMode);
  const activeDraggedEntryKey = getDraggedEntryKey(draggedEntry);

  useEffect(() => {
    spanResizeRef.current = spanResize;
  }, [spanResize]);

  const getCellContextFromPointer = useCallback(
    (event) => {
      if (typeof document === "undefined") return null;

      const clientX = Number(event?.clientX);
      const clientY = Number(event?.clientY);
      if (!Number.isFinite(clientX) || !Number.isFinite(clientY)) return null;

      const cells = document.querySelectorAll(".spv2-grid-cell[data-spv2-row-id][data-spv2-day-index]");
      for (const cell of cells) {
        const rect = cell.getBoundingClientRect();
        if (clientX < rect.left || clientX >= rect.right || clientY < rect.top || clientY >= rect.bottom) continue;

        const rowId = cell.getAttribute("data-spv2-row-id");
        const dayIndex = Number(cell.getAttribute("data-spv2-day-index"));
        const row = rows.find((candidate) => String(candidate.id) === String(rowId));
        const day = days[dayIndex];
        if (!row || !day) return null;

        return buildCellContext(row, day, rowMode);
      }

      return null;
    },
    [days, rowMode, rows]
  );

  /*
    Holt eine Zelle ins Bild. Genutzt vom Konflikte-Panel: wer dort einen
    Konflikt überfährt, sieht die betroffene Zelle auch dann, wenn sie
    ausserhalb des Ausschnitts liegt. Gescrollt wird nur, wenn die Zelle
    wirklich verdeckt ist, und nur so weit wie nötig.
  */
  const scrollCellIntoView = useCallback((rowId, dayIndex) => {
    const shell = gridScrollerRef.current;
    if (!shell) return;

    // dayIndex ist geprüft numerisch. Die Zeilen-ID kann beliebiger Text sein
    // (Abteilungsnamen) und wird darum verglichen statt in den Selektor gebaut.
    let cell = null;
    for (const candidate of shell.querySelectorAll(`.spv2-grid-cell[data-spv2-day-index="${dayIndex}"]`)) {
      if (candidate.getAttribute("data-spv2-row-id") === rowId) {
        cell = candidate;
        break;
      }
    }
    if (!cell) return;

    // Die Ecke oben links klebt in beiden Achsen und misst darum genau die
    // Fläche, die Kopfzeile und Namensspalte verdecken.
    const corner = shell.querySelector(".spv2-grid-corner");
    const shellRect = shell.getBoundingClientRect();
    const cellRect = cell.getBoundingClientRect();
    const shellStyle = window.getComputedStyle(shell);
    // clientWidth/clientHeight lassen Rahmen und Scrollbalken aussen vor.
    const innerLeft = shellRect.left + (parseFloat(shellStyle.borderLeftWidth) || 0);
    const innerTop = shellRect.top + (parseFloat(shellStyle.borderTopWidth) || 0);

    const deltaX = getRevealDelta({
      viewStart: innerLeft + (corner?.offsetWidth || 0),
      viewEnd: innerLeft + shell.clientWidth,
      targetStart: cellRect.left,
      targetEnd: cellRect.right,
      margin: FOCUS_SCROLL_MARGIN,
    });
    const deltaY = getRevealDelta({
      viewStart: innerTop + (corner?.offsetHeight || 0),
      viewEnd: innerTop + shell.clientHeight,
      targetStart: cellRect.top,
      targetEnd: cellRect.bottom,
      margin: FOCUS_SCROLL_MARGIN,
    });
    if (Math.abs(deltaX) < 1 && Math.abs(deltaY) < 1) return;

    const left = shell.scrollLeft + deltaX;
    const top = shell.scrollTop + deltaY;
    if (typeof shell.scrollTo !== "function") {
      shell.scrollLeft = left;
      shell.scrollTop = top;
      return;
    }

    shell.scrollTo({ left, top, behavior: prefersReducedMotion() ? "auto" : "smooth" });
  }, []);

  /*
    Haelt die zwei Proxy-Scrollbalken mit dem Raster-Scroller synchron. Der
    waagrechte Balken soll erst nach der klebenden Mitarbeitenden-Spalte
    beginnen — ein nativer Balken spannt immer ueber die volle Breite. Darum
    scrollt das Raster in einem Container ohne sichtbare Balken, und die
    sichtbaren Balken daneben sind eigene schmale Scroller, deren Spacer die
    Scrollflaeche des Rasters nachbildet.
  */
  const hasRows = rows.length > 0;

  useEffect(() => {
    if (!hasRows) return undefined;

    const scroller = gridScrollerRef.current;
    const hBar = hScrollbarRef.current;
    const vBar = vScrollbarRef.current;
    if (!scroller || !hBar || !vBar) return undefined;

    const shell = scroller.parentElement;

    // Das Echo des eigenen Schreibens darf den Scroller nicht anfassen: das
    // scroll-Ereignis des Balkens kommt einen Frame nach unserem Setzen. Bei
    // einer sanften Scroll-Animation (Konflikt-Hover, scrollTo smooth) ist der
    // Scroller dann schon weiter — ein ungefiltertes Zurueckschreiben wuerde
    // die veraltete Position setzen und die Animation abbrechen. Darum merken
    // wir uns den zuletzt geschriebenen Wert und ignorieren Ereignisse, die
    // ihn nur bestaetigen (Toleranz wegen Bruchteil-Pixeln beim Zoomen).
    let hEcho = -1;
    let vEcho = -1;
    const syncFromScroller = () => {
      hEcho = scroller.scrollLeft;
      vEcho = scroller.scrollTop;
      hBar.scrollLeft = hEcho;
      vBar.scrollTop = vEcho;
    };
    const syncFromHBar = () => {
      if (Math.abs(hBar.scrollLeft - hEcho) < 1) return;
      scroller.scrollLeft = hBar.scrollLeft;
    };
    const syncFromVBar = () => {
      if (Math.abs(vBar.scrollTop - vEcho) < 1) return;
      scroller.scrollTop = vBar.scrollTop;
    };

    const measure = () => {
      // Die Ecke oben links ist genauso breit wie die klebende Namensspalte.
      const inset = scroller.querySelector(".spv2-grid-corner")?.offsetWidth || 0;
      // Der Spacer wird um den Versatz gekuerzt, damit beide Scroller denselben
      // maximalen scrollLeft haben und die Kopplung 1:1 bleibt.
      const hSpacer = hBar.firstElementChild;
      const vSpacer = vBar.firstElementChild;
      if (hSpacer) hSpacer.style.width = `${Math.max(1, scroller.scrollWidth - inset)}px`;
      if (vSpacer) vSpacer.style.height = `${Math.max(1, scroller.scrollHeight)}px`;
      if (shell) {
        shell.style.setProperty("--spv2-hscrollbar-inset", `${inset}px`);
        shell.dataset.hscroll = scroller.scrollWidth - scroller.clientWidth > 1 ? "true" : "false";
        shell.dataset.vscroll = scroller.scrollHeight - scroller.clientHeight > 1 ? "true" : "false";
      }
      syncFromScroller();
    };

    scroller.addEventListener("scroll", syncFromScroller);
    hBar.addEventListener("scroll", syncFromHBar);
    vBar.addEventListener("scroll", syncFromVBar);

    let observer;
    let measureFrame = 0;
    if (typeof ResizeObserver === "function") {
      // Nicht direkt im Callback messen: measure() aendert selbst Layout
      // (Spacer, Balken ein-/ausblenden) und wuerde im selben Frame neue
      // Resize-Meldungen ausloesen — der Browser meldet das als
      // "ResizeObserver loop completed with undelivered notifications".
      // Der naechste Frame buendelt zudem Serien von Resize-Ereignissen.
      observer = new ResizeObserver(() => {
        cancelAnimationFrame(measureFrame);
        measureFrame = requestAnimationFrame(measure);
      });
      observer.observe(scroller);
      // Das Raster selbst ist die Scrollflaeche: waechst der Inhalt (mehr
      // Zeilen, anderer Zeitraum, Bedarfszeile), aendert sich seine Groesse.
      const grid = scroller.firstElementChild;
      if (grid) observer.observe(grid);
    }
    measure();

    return () => {
      scroller.removeEventListener("scroll", syncFromScroller);
      hBar.removeEventListener("scroll", syncFromHBar);
      vBar.removeEventListener("scroll", syncFromVBar);
      cancelAnimationFrame(measureFrame);
      observer?.disconnect();
    };
  }, [hasRows]);

  const focusRowId = String(focusCell?.rowId || "");
  const focusDateKey = String(focusCell?.dateKey || "");

  useEffect(() => {
    if (!focusRowId || !focusDateKey) return undefined;

    const dayIndex = days.findIndex((day) => String(day?.key || "") === focusDateKey);
    if (dayIndex < 0) return undefined;

    // Kurzer Verzug: beim Überfahren einer Liste soll nur das Ziel scrollen, auf
    // dem die Maus liegen bleibt — nicht jede Zeile auf dem Weg dorthin.
    const timer = window.setTimeout(() => scrollCellIntoView(focusRowId, dayIndex), FOCUS_SCROLL_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [days, focusDateKey, focusRowId, scrollCellIntoView]);

  const previewSpanResize = useCallback((rowId, dayIndex) => {
    setSpanResize((current) => {
      if (!current || String(current.rowId) !== String(rowId)) return current;

      const nextRange = getResizePreviewRange(current, dayIndex);
      if (nextRange.startIndex === current.previewStartIndex && nextRange.endIndex === current.previewEndIndex) {
        return current;
      }

      return {
        ...current,
        previewStartIndex: nextRange.startIndex,
        previewEndIndex: nextRange.endIndex,
      };
    });
  }, []);

  const previewSpanResizeFromPointer = useCallback(
    (event) => {
      if (!spanResizeRef.current || typeof document.elementFromPoint !== "function") return;

      const target = document.elementFromPoint(event.clientX, event.clientY);
      const cell = target?.closest?.(".spv2-grid-cell[data-spv2-row-id][data-spv2-day-index]");
      if (!cell) return;

      const rowId = cell.getAttribute("data-spv2-row-id");
      const dayIndex = Number(cell.getAttribute("data-spv2-day-index"));
      if (!Number.isFinite(dayIndex)) return;

      previewSpanResize(rowId, dayIndex);
    },
    [previewSpanResize]
  );

  useEffect(() => {
    const stopSelecting = () => {
      isSelectingRef.current = false;
      selectionAnchorRef.current = null;
    };

    window.addEventListener("mouseup", stopSelecting);
    return () => window.removeEventListener("mouseup", stopSelecting);
  }, []);

  useEffect(() => {
    if (!spanResize) return undefined;

    window.addEventListener("mousemove", previewSpanResizeFromPointer);
    return () => window.removeEventListener("mousemove", previewSpanResizeFromPointer);
  }, [previewSpanResizeFromPointer, spanResize]);

  useEffect(() => {
    const finishSpanResize = () => {
      const currentResize = spanResizeRef.current;
      if (!currentResize) return;

      spanResizeRef.current = null;
      setSpanResize(null);

      if (
        currentResize.previewStartIndex === currentResize.originalStartIndex &&
        currentResize.previewEndIndex === currentResize.originalEndIndex
      ) {
        return;
      }

      const startDate = days[currentResize.previewStartIndex]?.key;
      const endDate = days[currentResize.previewEndIndex]?.key;
      if (!startDate || !endDate) return;

      onSpanResizeCommit?.({
        entry: currentResize.entry,
        type: currentResize.type,
        row: currentResize.row,
        rowMode: currentResize.rowMode,
        startDate,
        endDate,
      });
    };

    const cancelSpanResize = (event) => {
      if (event.key !== "Escape" || !spanResizeRef.current) return;
      event.preventDefault();
      spanResizeRef.current = null;
      setSpanResize(null);
    };

    window.addEventListener("mouseup", finishSpanResize);
    window.addEventListener("keydown", cancelSpanResize);

    return () => {
      window.removeEventListener("mouseup", finishSpanResize);
      window.removeEventListener("keydown", cancelSpanResize);
    };
  }, [days, onSpanResizeCommit]);

  const startSpanResize = useCallback(
    ({ entry, type, edge, row, rowMode, startIndex, endIndex }) => {
      if (!onSpanResizeCommit || !entry || !row || !edge) return;

      const key = getSpanningEntryKey(type, entry);
      const bounds = buildResizeBounds(days.length);
      const resizeConfig = {
        edge,
        bounds,
        originalStartIndex: startIndex,
        originalEndIndex: endIndex,
        dayCount: days.length,
      };
      const initialRange = getResizePreviewRange(resizeConfig, edge === "start" ? startIndex : endIndex);

      isSelectingRef.current = false;
      selectionAnchorRef.current = null;
      setSpanResize({
        key,
        type,
        entry,
        edge,
        row,
        rowId: row.id,
        rowMode,
        originalStartIndex: startIndex,
        originalEndIndex: endIndex,
        previewStartIndex: initialRange.startIndex,
        previewEndIndex: initialRange.endIndex,
        bounds,
        dayCount: days.length,
      });
    },
    [days, onSpanResizeCommit]
  );

  const buildRangeSelection = (row, startIndex, endIndex) => {
    const from = Math.min(startIndex, endIndex);
    const to = Math.max(startIndex, endIndex);
    return days.slice(from, to + 1).map((day) => buildCellContext(row, day, rowMode));
  };

  if (!rows.length) {
    const emptyTitle = rowMode === "department" ? "Keine Abteilungen gefunden" : "Keine Mitarbeitenden gefunden";
    const emptyText =
      rowMode === "department"
        ? "Die aktuelle Ansicht enthält keine passenden Abteilungen."
        : "Die aktuelle Ansicht enthält keine passenden Mitarbeitenden.";

    return (
      <ShiftplanV2State
        title={emptyTitle}
        text={emptyText}
      />
    );
  }

  return (
    <Box className="spv2-grid-shell">
      <Box className="spv2-grid-scroller" ref={gridScrollerRef}>
      <Box
        className={`spv2-grid is-${viewMode === "week" ? "week" : "month"}-view${
          spanResize ? " is-span-resizing" : ""
        }`}
        role="grid"
        aria-label={viewMode === "week" ? "Shiftplan Wochenraster" : "Shiftplan Monatsraster"}
        style={{
          "--spv2-day-count": days.length,
          "--spv2-day-column-min": `${GRID_DAY_COLUMN_MIN_WIDTH}px`,
          "--spv2-grid-min-width": `${getGridMinWidth(days.length)}px`,
        }}
      >
        <Box className="spv2-grid-corner" role="columnheader">
          <Box component="span" className="spv2-grid-corner-label">
            {rowMode === "department" ? "Abteilungen" : "Mitarbeitende"}
          </Box>
          {demandAvailable ? (
            <ShiftplanTooltip title={demandVisible ? "Bedarfszeile ausblenden" : "Bedarfszeile anzeigen"}>
              <button
                type="button"
                className={`spv2-demand-corner-toggle${demandVisible ? " is-active" : ""}`}
                aria-pressed={demandVisible}
                aria-label={demandVisible ? "Bedarfszeile ausblenden" : "Bedarfszeile anzeigen"}
                onClick={() => onDemandVisibleChange?.(!demandVisible)}
              >
                {demandVisible ? "– Bedarf" : "+ Bedarf"}
              </button>
            </ShiftplanTooltip>
          ) : null}
        </Box>

        {days.map((day) => {
          const isWeekView = viewMode === "week";
          const dayLabel = isWeekView ? day.dayMonthLabel : day.dayNumber;
          const weekdayLabel = isWeekView ? day.weekdayLong || day.weekdayShort : day.weekdayShort;
          const holidayNames = publicHolidayNamesByDate[day.key] || [];
          const holidayTitle = holidayNames.join(", ");
          const dayComment = String(dayCommentsByDate[day.key] || "");
          const dayCommentLabel = `${day.weekdayShort} ${day.dayMonthLabel}`.trim();
          const isCommentOpen = dayCommentTarget?.dayKey === day.key;

          const dayHead = (
            <Box
              key={day.key}
              className={`spv2-grid-day-head${canEditDayComments ? " is-clickable" : ""}${day.isWeekend ? " is-weekend" : ""}${
                day.isToday ? " is-today" : ""
              }${holidayNames.length ? " is-public-holiday" : ""}${isCommentOpen ? " is-comment-open" : ""}`}
              role="columnheader"
              aria-haspopup={canEditDayComments ? "dialog" : undefined}
              aria-expanded={canEditDayComments ? isCommentOpen : undefined}
              onClick={
                canEditDayComments
                  ? (event) =>
                      setDayCommentTarget({ dayKey: day.key, label: dayCommentLabel, anchorEl: event.currentTarget })
                  : undefined
              }
            >
              <Typography className="spv2-grid-day-weekday">{weekdayLabel}</Typography>
              <Typography className="spv2-grid-day-number">{dayLabel}</Typography>
              {dayComment ? (
                <Box
                  className={`spv2-grid-day-comment-marker${holidayNames.length ? " has-holiday-neighbor" : ""}`}
                  aria-label={`Tagesnotiz: ${dayComment}`}
                >
                  <StickyNote2OutlinedIcon fontSize="inherit" />
                </Box>
              ) : null}
              {holidayNames.length ? (
                <ShiftplanTooltip title={holidayTitle}>
                  <Box className="spv2-grid-holiday-marker" aria-label={`Feiertag: ${holidayTitle}`}>
                    <CelebrationOutlinedIcon fontSize="inherit" />
                  </Box>
                </ShiftplanTooltip>
              ) : null}
            </Box>
          );

          // Mit Kommentar zeigt bereits die ganze Datumszelle den Tooltip.
          return dayComment ? (
            <ShiftplanTooltip key={day.key} title={dayComment}>
              {dayHead}
            </ShiftplanTooltip>
          ) : (
            dayHead
          );
        })}

        <DayCommentPopover
          open={Boolean(dayCommentTarget)}
          anchorEl={dayCommentTarget?.anchorEl || null}
          dayLabel={dayCommentTarget?.label || ""}
          comment={String(dayCommentsByDate[dayCommentTarget?.dayKey] || "")}
          onSave={(comment) => onSaveDayComment?.(dayCommentTarget?.dayKey, comment)}
          onClose={() => setDayCommentTarget(null)}
        />

        {demandAvailable && demandVisible ? (
          <DemandRow
            days={days}
            demandByDay={demandByDay}
            expanded={demandExpanded}
            compact={demandCompact}
            totalMissing={demandTotalMissing}
            totalOver={demandTotalOver}
            canDrag={canDragDemand}
            draggedDemandId={isDemandDragEntry(draggedEntry) ? String(draggedEntry.demandId || "") : ""}
            draggedShiftDateKey={
              draggedEntry?.entryType === "shift" && !isDemandDragEntry(draggedEntry)
                ? String(draggedEntry.dateKey || "")
                : ""
            }
            onToggleExpanded={onDemandToggleExpanded}
            onDemandDragStart={onDemandDragStart}
            onDemandDragEnd={onDemandDragEnd}
            onShiftDropOnDemand={onShiftDropOnDemand}
          />
        ) : null}

        {rows.map((row, rowIndex) => {
          const workload = workloadByEmployeeId[row.id] || EMPTY_WORKLOAD;
          const ShiftCard = rowMode === "department" ? DepartmentShiftChip : ShiftChip;
          const useCompactShiftLabels = viewMode === "month";
          const maxCollapsedShifts = viewMode === "month"
            ? MAX_COLLAPSED_SHIFTS_PER_CELL_MONTH
            : MAX_COLLAPSED_SHIFTS_PER_CELL_WEEK;
          const isLastRow = rowIndex === rows.length - 1;
          const showSections = rowMode !== "department" && Boolean(employeeSections);
          const isOwnRow =
            showSections && Boolean(currentEmployeeId) && String(row.id) === String(currentEmployeeId);
          const isFirstTeamRow =
            showSections
            && !isOwnRow
            && rows.findIndex((item) => String(item.id) !== String(currentEmployeeId)) === rowIndex;
          const rowEntryRanges = buildRowEntryRanges({ row, rowMode, days, shiftsByCell, userHolidaysByCell });
          const baseSpanningEntries = buildSpanningEntriesFromRanges(rowEntryRanges);
          const dragTargetStartIndex = getDragTargetStartIndex({
            row,
            days,
            dragTargetCellKey,
            dragAnchorDayOffset,
          });
          const spanningEntries = applySpanResizePreview(baseSpanningEntries, rowEntryRanges, spanResize, row.id);
          const spanningLookup = buildSpanningEntryLookup(spanningEntries, days.length);

          return (
            <React.Fragment key={row.id}>
              {isOwnRow ? (
                <Box className="spv2-grid-section-band is-own" role="presentation">
                  <span className="spv2-grid-section-label">{employeeSections.ownLabel}</span>
                </Box>
              ) : null}
              {isFirstTeamRow ? (
                <Box className="spv2-grid-section-band is-team" role="presentation">
                  <span className="spv2-grid-section-label">
                    {employeeSections.teamLabel}
                    <span className="spv2-grid-section-meta">
                      {" · "}
                      {teamCount === 1 ? "1 Person" : `${teamCount} Personen`}
                    </span>
                  </span>
                </Box>
              ) : null}
              <Box className={`spv2-grid-employee-cell${isLastRow ? " is-last-row" : ""}`} role="rowheader">
                {rowMode === "department" ? (
                  <DepartmentRowContent row={row} />
                ) : showSections ? (
                  <EmployeeViewRowContent row={row} workload={workload} isOwn={isOwnRow} />
                ) : (
                  <EmployeeRowContent
                    row={row}
                    workload={workload}
                    onStatsOpen={onEmployeeStatsOpen ? () => onEmployeeStatsOpen(row) : undefined}
                  />
                )}
              </Box>

              {days.map((day, dayIndex) => {
                const key = `${row.id}::${day.key}`;
                const cellShifts = (shiftsByCell[key] || []).filter(
                  (shift) => shift?.entryType !== "absence" || !spanningLookup.absenceKeys.has(getAbsenceSpanKey(shift))
                );
                const hasMultipleShifts = cellShifts.length > 1;
                const hasOverflowShifts = cellShifts.length > maxCollapsedShifts;
                const isExpandedShiftCell = hasOverflowShifts && expandedShiftCellKeys.has(key);
                const visibleShifts = isExpandedShiftCell
                  ? cellShifts
                  : cellShifts.slice(0, maxCollapsedShifts);
                const hiddenCount = isExpandedShiftCell
                  ? 0
                  : Math.max(0, cellShifts.length - maxCollapsedShifts);
                const isPublicHoliday = Boolean(publicHolidayNamesByDate[day.key]?.length);
                const cellContext = buildCellContext(row, day, rowMode);
                const isInteractive = Boolean(onCellSelect || onCellDoubleClick || onCellContextMenu || spanResize);
                const isSelected = selectedCellKeys.has(key);
                const isDragTarget =
                  Boolean(activeDraggedEntryKey) &&
                  isDayInsideDragTarget({
                    draggedEntry,
                    dragTargetStartIndex,
                    dayIndex,
                    dayKey: day.key,
                  });
                const availability = showAvailability ? availabilityByCell[key] : null;
                const cellUserHolidays = rowMode === "employee"
                  ? (userHolidaysByCell[key] || []).filter(
                      (holiday) => !spanningLookup.holidayKeys.has(getUserHolidaySpanKey(holiday))
                    )
                  : [];
                const spanningEntriesStartingToday = spanningLookup.byStartIndex[dayIndex] || [];
                const spanningCoverCount = spanningLookup.coverCounts[dayIndex] || 0;
                const topEntryCount = spanningCoverCount + cellUserHolidays.length;
                const cellStyle = topEntryCount
                  ? {
                      "--spv2-top-entry-space": getTopEntrySpace(topEntryCount),
                      "--spv2-user-holiday-stack-top": `${
                        SPANNING_ENTRY_TOP + spanningCoverCount * (SPANNING_ENTRY_HEIGHT + SPANNING_ENTRY_GAP)
                      }px`,
                    }
                  : undefined;
                const handleCellSelectStart = (event) => {
                  if (spanResize) return;
                  if (event.button !== 0) return;

                  const isToggle = event.ctrlKey || event.metaKey;
                  const isRange = event.shiftKey;
                  const rangeAnchor = lastSelectionAnchorRef.current;

                  if (isRange && rangeAnchor?.row?.id === row.id) {
                    event.preventDefault();
                    event.stopPropagation();
                    const range = buildRangeSelection(row, rangeAnchor.dayIndex, dayIndex);
                    const shouldAddRange = isToggle || selectedCellKeys.size > 1;
                    onCellRangeSelect?.(range, { mode: shouldAddRange ? "add" : "replace" });
                    isSelectingRef.current = false;
                    selectionAnchorRef.current = null;
                    lastSelectionAnchorRef.current = { row, dayIndex };
                    return;
                  }

                  if (isToggle) {
                    event.preventDefault();
                    event.stopPropagation();
                    onCellSelect?.(cellContext, { mode: "toggle" });
                    isSelectingRef.current = false;
                    selectionAnchorRef.current = null;
                    lastSelectionAnchorRef.current = { row, dayIndex };
                    return;
                  }

                  isSelectingRef.current = true;
                  selectionAnchorRef.current = { row, dayIndex };
                  lastSelectionAnchorRef.current = { row, dayIndex };
                  onCellSelect?.(cellContext);
                };
                const handleCellEnter = () => {
                  if (spanResize) {
                    previewSpanResize(row.id, dayIndex);
                    return;
                  }

                  const selectionAnchor = selectionAnchorRef.current;
                  if (!isSelectingRef.current || !selectionAnchor || selectionAnchor.row.id !== row.id) return;
                  onCellRangeSelect?.(buildRangeSelection(row, selectionAnchor.dayIndex, dayIndex));
                };
                const handleCellOpen = (event) => {
                  if (spanResize) return;
                  event.preventDefault();
                  event.stopPropagation();
                  onCellDoubleClick?.(cellContext);
                };
                const handleCellContextMenu = (event) => {
                  if (spanResize) return;
                  onCellContextMenu?.(event, cellContext);
                };
                const handleCellDragTarget = (event) => {
                  if (!activeDraggedEntryKey) return;
                  event.preventDefault();
                  if (event.dataTransfer) event.dataTransfer.dropEffect = "move";
                  onCellDragTarget?.(getCellContextFromPointer(event) || cellContext);
                };
                const handleCellDrop = (event) => {
                  if (!activeDraggedEntryKey) return;
                  event.preventDefault();
                  event.stopPropagation();
                  onCellDropEntry?.(getCellContextFromPointer(event) || cellContext);
                };
                const handleShiftOverflowToggle = (event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  setExpandedShiftCellKeys((currentKeys) => {
                    const nextKeys = new Set(currentKeys);
                    if (nextKeys.has(key)) {
                      nextKeys.delete(key);
                    } else {
                      nextKeys.add(key);
                    }
                    return nextKeys;
                  });
                };

                const isShiftSelected = (shift) => {
                  const shiftId = String(shift?.id || "");
                  return selectedShiftIds instanceof Set
                    ? selectedShiftIds.has(shiftId)
                    : String(selectedShiftId) === shiftId;
                };
                const isShiftCopied = (shift) => {
                  const shiftId = String(shift?.id || "");
                  return copiedTimetableEntryIds instanceof Set
                    ? copiedTimetableEntryIds.has(shiftId)
                    : String(copiedTimetableEntryId) === shiftId;
                };
                const getSingleDayResizeProps = (type, entry) => {
                  if (!onSpanResizeCommit || !isSingleDayEntryOnDay(type, entry, day.key)) return null;

                  return {
                    canResizeStart: true,
                    canResizeEnd: true,
                    onResizeStart: (edge) =>
                      startSpanResize({
                        entry,
                        type,
                        edge,
                        row,
                        rowMode,
                        startIndex: dayIndex,
                        endIndex: dayIndex,
                      }),
                  };
                };

                return (
                  <Box
                    key={key}
                    className={`spv2-grid-cell${day.isWeekend ? " is-weekend" : ""}${day.isToday ? " is-today" : ""}${
                      isPublicHoliday ? " is-public-holiday" : ""
                    }${isInteractive ? " is-interactive" : ""}${isSelected ? " is-selected" : ""}${
                      isDragTarget ? " is-drag-target" : ""
                    }${hasMultipleShifts ? " has-multiple-shifts" : ""}${
                      hasOverflowShifts ? " has-overflow-shifts" : ""
                    }${isExpandedShiftCell ? " is-expanded" : ""}${isLastRow ? " is-last-row" : ""}${
                      cellUserHolidays.length ? " has-user-holiday" : ""
                    }${spanningCoverCount ? " has-spanning-entry" : ""}${
                      spanningEntriesStartingToday.length ? " has-spanning-entry-start" : ""
                    }${
                      availability
                        ? ` has-availability is-availability-${availability.status} is-availability-mode-${
                            availability.mode || "absence"
                          } is-availability-kind-${availability.kind || "availability"}`
                        : ""
                    }`}
                    role="gridcell"
                    tabIndex={isInteractive ? 0 : undefined}
                    data-spv2-row-id={row.id}
                    data-spv2-day-index={dayIndex}
                    style={cellStyle}
                    onMouseDown={isInteractive ? handleCellSelectStart : undefined}
                    onMouseEnter={isInteractive ? handleCellEnter : undefined}
                    onDoubleClick={isInteractive ? handleCellOpen : undefined}
                    onContextMenu={isInteractive ? handleCellContextMenu : undefined}
                    onDragEnter={activeDraggedEntryKey ? handleCellDragTarget : undefined}
                    onDragOver={activeDraggedEntryKey ? handleCellDragTarget : undefined}
                    onDrop={activeDraggedEntryKey ? handleCellDrop : undefined}
                    onKeyDown={isInteractive ? (event) => handleKeyboardActivation(event, handleCellOpen) : undefined}
                  >
                    {spanningEntriesStartingToday.map((spanningEntry) => {
                      const spanEntry = spanningEntry.entry;
                      const spanDays = spanningEntry.endIndex - spanningEntry.startIndex + 1;
                      const isUserHolidaySpan = spanningEntry.type === "user-holiday";
                      const isDraggedSpan =
                        Boolean(activeDraggedEntryKey) &&
                        getDraggedEntryKey(spanEntry) === activeDraggedEntryKey;
                      const isSpanSelected = isUserHolidaySpan
                        ? Boolean(selectedUserHolidayId) && String(spanEntry.id) === String(selectedUserHolidayId)
                        : isShiftSelected(spanEntry);
                      const isSpanCopied = isUserHolidaySpan
                        ? Boolean(copiedUserHolidayId) && String(spanEntry.id) === String(copiedUserHolidayId)
                        : isShiftCopied(spanEntry);
                      const isSpanReleasePreviewTarget =
                        !isUserHolidaySpan &&
                        hasReleasePreview &&
                        previewShiftIds.has(String(spanEntry.id));
                      const isSpanResizing =
                        spanResize?.key === spanningEntry.key &&
                        spanResize?.type === spanningEntry.type &&
                        String(spanResize?.rowId) === String(row.id);
                      const canResizeSpan = Boolean(onSpanResizeCommit);
                      const canResizeStart =
                        canResizeSpan &&
                        getSpanningEntryStartDateKey(spanningEntry.type, spanEntry) === days[spanningEntry.startIndex]?.key;
                      const canResizeEnd =
                        canResizeSpan &&
                        getSpanningEntryEndDateKey(spanningEntry.type, spanEntry) === days[spanningEntry.endIndex]?.key;
                      const spanSelectHandler = isUserHolidaySpan
                        ? () => onUserHolidaySelect?.({ holiday: spanEntry, row, day, rowMode })
                        : (event) => onShiftSelect?.({ shift: spanEntry, row, day, rowMode }, event);
                      const spanOpenHandler = isUserHolidaySpan
                        ? () => onUserHolidayDoubleClick?.({ holiday: spanEntry, row, day, rowMode })
                        : () => onShiftDoubleClick?.({ shift: spanEntry, row, day, rowMode });
                      const spanContextMenuHandler = isUserHolidaySpan
                        ? (event) => onUserHolidayContextMenu?.(event, { holiday: spanEntry, row, day, rowMode })
                        : (event) => onShiftContextMenu?.(event, { shift: spanEntry, row, day, rowMode });

                      return (
                        <SpanningEntryMarker
                          key={spanningEntry.key}
                          entry={spanEntry}
                          type={spanningEntry.type}
                          spanDays={spanDays}
                          lane={spanningEntry.lane}
                          showEmployee={rowMode === "department"}
                          selected={isSpanSelected}
                          copied={isSpanCopied}
                          dragging={isDraggedSpan}
                          resizing={isSpanResizing}
                          releasePreviewActive={hasReleasePreview}
                          releasePreviewTarget={isSpanReleasePreviewTarget}
                          canResizeStart={canResizeStart}
                          canResizeEnd={canResizeEnd}
                          onSelect={spanSelectHandler}
                          onOpen={spanOpenHandler}
                          onContextMenu={spanContextMenuHandler}
                          onDragStart={
                            onEntryDragStart
                              ? (event, dragDetails) =>
                                  onEntryDragStart({
                                    entry: spanEntry,
                                    shift: isUserHolidaySpan ? undefined : spanEntry,
                                    holiday: isUserHolidaySpan ? spanEntry : undefined,
                                    row,
                                    day,
                                    rowMode,
                                    dragDayOffset: dragDetails?.dragDayOffset || 0,
                                  })
                              : undefined
                          }
                          onDragEnd={
                            onEntryDragEnd
                              ? () =>
                                  onEntryDragEnd({
                                    entry: spanEntry,
                                    shift: isUserHolidaySpan ? undefined : spanEntry,
                                    holiday: isUserHolidaySpan ? spanEntry : undefined,
                                    row,
                                    day,
                                    rowMode,
                                  })
                              : undefined
                          }
                          onResizeStart={
                            canResizeSpan
                              ? (edge) =>
                                  startSpanResize({
                                    entry: spanEntry,
                                    type: spanningEntry.type,
                                    edge,
                                    row,
                                    rowMode,
                                    startIndex: spanningEntry.startIndex,
                                    endIndex: spanningEntry.endIndex,
                                  })
                              : undefined
                          }
                        />
                      );
                    })}
                    <UserHolidayMarker
                      holidays={cellUserHolidays}
                      selectedHolidayId={selectedUserHolidayId}
                      copiedHolidayId={copiedUserHolidayId}
                      draggedEntryKey={activeDraggedEntryKey}
                      releasePreviewActive={hasReleasePreview}
                      onSelect={
                        onUserHolidaySelect
                          ? (holiday) => onUserHolidaySelect({ holiday, row, day, rowMode })
                          : undefined
                      }
                      onOpen={
                        onUserHolidayDoubleClick
                          ? (holiday) => onUserHolidayDoubleClick({ holiday, row, day, rowMode })
                          : undefined
                      }
                      onContextMenu={
                        onUserHolidayContextMenu
                          ? (event, holiday) => onUserHolidayContextMenu(event, { holiday, row, day, rowMode })
                          : undefined
                      }
                      onDragStart={
                        onEntryDragStart
                          ? (holiday, dragDetails) =>
                              onEntryDragStart({
                                entry: holiday,
                                holiday,
                                row,
                                day,
                                rowMode,
                                dragDayOffset: dragDetails?.dragDayOffset || 0,
                              })
                          : undefined
                      }
                      onDragEnd={
                        onEntryDragEnd
                          ? (holiday) =>
                              onEntryDragEnd({
                                entry: holiday,
                                holiday,
                                row,
                                day,
                                rowMode,
                              })
                          : undefined
                      }
                      getResizeProps={(holiday) => getSingleDayResizeProps("user-holiday", holiday)}
                    />
                    <AvailabilityMarker availability={availability} />
                    {visibleShifts.map((shift) => {
                      const resizeProps =
                        shift?.entryType === "absence" ? getSingleDayResizeProps("absence", shift) : null;

                      return (
                        <ShiftCard
                          key={shift.id}
                          shift={shift}
                          compactLabels={useCompactShiftLabels}
                          shiftDisplayMode={normalizedShiftDisplayMode}
                          selected={isShiftSelected(shift)}
                          copied={isShiftCopied(shift)}
                          dragging={Boolean(activeDraggedEntryKey) && getDraggedEntryKey(shift) === activeDraggedEntryKey}
                          releasePreviewActive={hasReleasePreview}
                          releasePreviewTarget={hasReleasePreview && previewShiftIds.has(String(shift.id))}
                          canResizeStart={Boolean(resizeProps?.canResizeStart)}
                          canResizeEnd={Boolean(resizeProps?.canResizeEnd)}
                          onSelect={(event) => onShiftSelect?.({ shift, row, day, rowMode }, event)}
                          onOpen={() => onShiftDoubleClick?.({ shift, row, day, rowMode })}
                          onContextMenu={(event) => onShiftContextMenu?.(event, { shift, row, day, rowMode })}
                          // Ohne Handler (Mitarbeitenden-Ansicht) bleibt die Karte unverschiebbar.
                          onDragStart={
                            onEntryDragStart
                              ? () => onEntryDragStart({ entry: shift, shift, row, day, rowMode, dragDayOffset: 0 })
                              : undefined
                          }
                          onDragEnd={onEntryDragEnd ? () => onEntryDragEnd({ entry: shift, shift, row, day, rowMode }) : undefined}
                          onResizeStart={resizeProps?.onResizeStart}
                        />
                      );
                    })}
                    {hasOverflowShifts ? (
                      <Box
                        component="button"
                        type="button"
                        className={`spv2-grid-more${isExpandedShiftCell ? " is-expanded" : ""}`}
                        aria-label={
                          isExpandedShiftCell
                            ? "Zusätzliche Schichten einklappen"
                            : `${hiddenCount} weitere Schichten anzeigen`
                        }
                        aria-expanded={isExpandedShiftCell}
                        onMouseDown={stopGridPointerSelection}
                        onDoubleClick={stopGridPointerSelection}
                        onKeyDown={stopGridPointerSelection}
                        onClick={handleShiftOverflowToggle}
                      >
                        {isExpandedShiftCell ? "-" : `+${hiddenCount}`}
                      </Box>
                    ) : null}
                  </Box>
                );
              })}
            </React.Fragment>
          );
        })}
      </Box>
      </Box>
      {/* Proxy-Scrollbalken, synchron zum versteckten Scroller (Effekt oben).
          aria-hidden: reine Bedienelemente fuer die Maus, das Raster selbst
          bleibt per Tastatur/Rad scrollbar. */}
      <div className="spv2-grid-scrollbar spv2-grid-scrollbar--y" ref={vScrollbarRef} aria-hidden="true">
        <div className="spv2-grid-scrollbar-spacer" />
      </div>
      <div className="spv2-grid-scrollbar spv2-grid-scrollbar--x" ref={hScrollbarRef} aria-hidden="true">
        <div className="spv2-grid-scrollbar-spacer" />
      </div>
    </Box>
  );
}
